import { ScaleElement } from '@scale/base-element';

class ScaleSlider extends ScaleElement {
  static #resizes = new ResizeObserver((entries) => {
    const sliders = entries.map(({ target }) => target.closest('scale-slider'));
    const layouts = sliders.map((slider) => slider.#measure());
    sliders.forEach((slider, i) => slider.#render(layouts[i]));
  });

  #track = null;
  #slides = [];
  #dots = [];
  #progressFill = null;
  #paginationSignature = '';
  #currentIndex = 0;
  #autoplayTimer = null;
  #autoplayStopped = false;
  #observer = null;
  #isVisible = true;

  setup() {
    const track = this.ref('track');
    if (!track) return;

    this.#track = track;
    this.#slides = [...track.children];

    const prev = this.ref('prev');
    const next = this.ref('next');
    const pagination = this.ref('pagination');
    if (prev) this.on('click', () => this.#handleArrow(-1), { target: prev });
    if (next) this.on('click', () => this.#handleArrow(1), { target: next });
    if (pagination) {
      this.on('click', (event) => {
        const target = event.target instanceof Element
          ? event.target.closest('.scale-slider__dot')
          : null;
        if (!target || !pagination.contains(target)) return;
        this.#scrollToIndex(Number(target.dataset.index));
        this.#stopAutoplay();
      }, { target: pagination });
    }

    this.on('scroll', () => this.debounce('scroll', () => this.#update(), 100), { target: track });

    this.#setupDrag(track);
    this.#setupAutoplay();

    ScaleSlider.#resizes.observe(track);

    let lastMobile = this.isMobile;
    this.mediaQuery('(width >= 750px)', () => {
      const nowMobile = this.isMobile;
      if (nowMobile === lastMobile) return;
      lastMobile = nowMobile;
      this.#update();
    });

    if (Shopify?.designMode) {
      this.onDocument('shopify:block:select', (e) => {
        const block = this.querySelector(`#shopify-block-${e.detail.blockId}`);
        if (!block) return;
        const slide = this.#slides.find(s => s === block || s.contains(block));
        if (!slide) return;
        this.#stopAutoplay();
        this.#scrollToIndex(this.#slides.indexOf(slide));
      });
    }
  }

  teardown() {
    this.#pauseAutoplay();
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#track) ScaleSlider.#resizes.unobserve(this.#track);
    this.#track = null;
    this.#slides = [];
    this.#dots = [];
    this.#progressFill = null;
    this.#paginationSignature = '';
  }


  #handleArrow(direction) {
    this.#scroll(direction);
    this.#stopAutoplay();
  }

  #scroll(direction) {
    const track = this.ref('track');
    const slideWidth = this.#getSlideWidth(track);
    if (!slideWidth) return;

    if (this.hasAttribute('data-rewind')) {
      const currentIndex = this.#indexAt(track.scrollLeft, slideWidth);
      const maxIndex = this.#slides.length - Math.floor(this.#getPerPage());
      if (direction === 1 && currentIndex >= maxIndex) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (direction === -1 && currentIndex <= 0) {
        track.scrollTo({ left: maxIndex * slideWidth, behavior: 'smooth' });
        return;
      }
    }

    const isRTL = getComputedStyle(track).direction === 'rtl';
    track.scrollBy({ left: (isRTL ? -direction : direction) * slideWidth, behavior: 'smooth' });
  }

  #scrollToIndex(index) {
    const slide = this.#slides[index];
    if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  #getSlideWidth(track) {
    const slide = track.firstElementChild;
    if (!slide) return 0;
    return slide.offsetWidth + (parseInt(getComputedStyle(track).gap) || 0);
  }

  #indexAt(scrollLeft, slideWidth) {
    return slideWidth ? Math.round(Math.abs(scrollLeft) / slideWidth) : 0;
  }

  #getPerPage() {
    const attr = this.isMobile ? 'data-per-page-mobile' : 'data-per-page-desktop';
    return this.getNumber(attr, 1);
  }

  #getPageCount() {
    return Math.max(1, this.#slides.length - Math.floor(this.#getPerPage()) + 1);
  }

  #showArrows() {
    const attr = this.isMobile ? 'data-arrows-mobile' : 'data-arrows-desktop';
    return this.getBoolean(attr);
  }

  #getPaginationType() {
    const desktop = this.getAttribute('data-pagination-desktop') || 'none';
    if (!this.isMobile) return desktop;
    const mobile = this.getAttribute('data-pagination-mobile') || 'same_as_desktop';
    return mobile === 'same_as_desktop' ? desktop : mobile;
  }


  #measure() {
    const track = this.#track;
    return {
      slideWidth: this.#getSlideWidth(track),
      scrollLeft: track.scrollLeft,
      canNavigate: track.scrollWidth > track.clientWidth + 1,
      atEnd: track.scrollLeft + track.clientWidth >= track.scrollWidth - 1,
    };
  }

  #update() {
    this.#render(this.#measure());
  }

  #render({ slideWidth, scrollLeft, canNavigate, atEnd }) {
    this.#currentIndex = this.#indexAt(scrollLeft, slideWidth);

    const nav = this.ref('nav');
    const prev = this.ref('prev');
    const next = this.ref('next');

    if (nav) nav.hidden = !canNavigate || !this.#showArrows();

    if (this.hasAttribute('data-rewind')) {
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
    } else {
      if (prev) prev.disabled = scrollLeft <= 1;
      if (next) next.disabled = atEnd;
    }

    this.#rebuildPagination(canNavigate);
    this.#updatePagination(canNavigate);
  }


  #setupDrag(track) {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    let from = 0;
    let scroll = 0;
    let holding = false;
    let pulled = false;

    this.on('pointerdown', (event) => {
      if (event.button !== 0) return;
      holding = true;
      pulled = false;
      from = event.clientX;
      scroll = track.scrollLeft;
    }, { target: track });

    this.on('pointermove', (event) => {
      if (!holding) return;
      const travelled = event.clientX - from;
      if (!pulled && Math.abs(travelled) < 4) return;
      pulled = true;
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
      track.scrollLeft = scroll - travelled;
    }, { target: track });

    const release = () => {
      holding = false;
      track.classList.remove('is-dragging');
    };
    this.on('pointerup', release, { target: track });
    this.on('pointercancel', release, { target: track });
    this.on('dragstart', (event) => event.preventDefault(), { target: track });
    this.on('click', (event) => {
      if (!pulled) return;
      event.preventDefault();
      event.stopPropagation();
    }, { target: track, capture: true });
  }

  #setupPagination(canNavigate) {
    const container = this.ref('pagination');
    if (!container) return;

    const type = this.#getPaginationType();
    const pageCount = this.#getPageCount();
    container.classList.toggle('scale-slider__pagination--dots', type === 'dots');
    container.classList.toggle('scale-slider__pagination--progress', type === 'progress');
    container.hidden = type === 'none' || pageCount <= 1 || !canNavigate;
    if (container.hidden) return;

    if (type === 'dots') {
      const slideLabel = this.getAttribute('data-slide-label');
      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'scale-slider__dot';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', `${slideLabel} ${i + 1}`);
        container.appendChild(dot);
      }
      this.#dots = [...container.querySelectorAll('.scale-slider__dot')];
    } else if (type === 'progress') {
      const fill = document.createElement('div');
      fill.className = 'scale-slider__progress-fill';
      container.appendChild(fill);
      this.#progressFill = fill;
    }
  }

  #updatePagination(canNavigate) {
    for (let i = 0; i < this.#dots.length; i++) {
      const active = i === this.#currentIndex;
      this.#dots[i].classList.toggle('active', active);
      if (active) {
        this.#dots[i].setAttribute('aria-current', 'true');
      } else {
        this.#dots[i].removeAttribute('aria-current');
      }
    }

    if (this.#progressFill) {
      const pageCount = this.#getPageCount();
      const pct = pageCount > 1 ? ((this.#currentIndex + 1) / pageCount) * 100 : 100;
      this.#progressFill.style.width = `${pct}%`;
    }

    const container = this.ref('pagination');
    if (container) {
      container.hidden = this.#getPaginationType() === 'none' || !canNavigate;
    }
  }

  #rebuildPagination(canNavigate) {
    const container = this.ref('pagination');
    if (!container) return;
    const signature = `${this.#getPaginationType()}:${this.#getPageCount()}:${canNavigate}`;
    if (signature === this.#paginationSignature) return;
    this.#paginationSignature = signature;
    container.innerHTML = '';
    this.#dots = [];
    this.#progressFill = null;
    this.#setupPagination(canNavigate);
  }


  #setupAutoplay() {
    const speed = this.getNumber('data-autoplay', 0);
    if (!speed || this.#slides.length <= 1) return;

    this.mediaQuery('(prefers-reduced-motion: reduce)', (matches) => {
      if (matches) {
        this.#pauseAutoplay();
      } else if (!this.#autoplayStopped) {
        this.#resumeAutoplay();
      }
    });

    this.on('mouseenter', () => this.#pauseAutoplay());
    this.on('mouseleave', () => {
      if (!this.#autoplayStopped) this.#resumeAutoplay();
    });

    this.on('focusin', () => this.#pauseAutoplay());
    this.on('focusout', (e) => {
      if (!this.contains(e.relatedTarget) && !this.#autoplayStopped) this.#resumeAutoplay();
    });

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#isVisible = entry.isIntersecting;
      if (entry.isIntersecting && !this.#autoplayStopped) {
        this.#resumeAutoplay();
      } else {
        this.#pauseAutoplay();
      }
    }, { threshold: 0.5 });
    this.#observer.observe(this);
  }

  #resumeAutoplay() {
    if (this.#autoplayTimer || this.#autoplayStopped || !this.#isVisible) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const speed = this.getNumber('data-autoplay', 0) * 1000;
    if (!speed) return;
    this.#autoplayTimer = setInterval(() => this.#scroll(1), speed);
  }

  #pauseAutoplay() {
    if (!this.#autoplayTimer) return;
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  #stopAutoplay() {
    this.#pauseAutoplay();
    this.#autoplayStopped = true;
  }
}

if (!customElements.get('scale-slider')) {
  customElements.define('scale-slider', ScaleSlider);
}
